import { useCallback, useEffect, useState } from "react"
import type { RefObject } from "react"

export interface ScrollOverflow {
  /** True when the element's content is taller than its client box. */
  overflowing: boolean
  /** True when the element is scrolled to (or past) its bottom edge. */
  atEnd: boolean
}

/**
 * `useScrollOverflow` — observe a scroll container and report whether its
 * content overflows vertically and whether it is scrolled to the end.
 *
 * Detection is deterministic, not a magic-px heuristic:
 *   - `overflowing` ⇔ `scrollHeight − clientHeight > 1`
 *   - `atEnd`       ⇔ `scrollTop + clientHeight ≥ scrollHeight − 1`
 * (the ±1 slack absorbs sub-pixel rounding on fractional-DPR displays).
 *
 * Measurement runs on scroll and on any size change, via a `ResizeObserver`
 * over the element (covers content reflow, viewport rotation, and OS
 * font-scaling). The initial measurement is delivered by the observer's
 * first callback rather than synchronously in the effect body, so the hook
 * never trips `react-hooks/set-state-in-effect`. The default state
 * (`{ overflowing: false, atEnd: true }`) is the fit-first safe default:
 * consumers show no affordance until genuine overflow is observed, which is
 * also the graceful fallback in environments without `ResizeObserver`.
 *
 * Used by `components/coins/redeem-sheet.tsx` to drive the "more below" cue
 * on the locked / can't-afford reward sheet (OKS-645) — the cue appears only
 * on real overflow and hides once the body is scrolled to its end.
 */
export function useScrollOverflow(
  ref: RefObject<HTMLElement | null>,
): ScrollOverflow {
  const [state, setState] = useState<ScrollOverflow>({
    overflowing: false,
    atEnd: true,
  })

  const measure = useCallback(() => {
    const el = ref.current
    if (!el) return
    const { scrollTop, scrollHeight, clientHeight } = el
    const overflowing = scrollHeight - clientHeight > 1
    const atEnd = scrollTop + clientHeight >= scrollHeight - 1
    setState((prev) =>
      prev.overflowing === overflowing && prev.atEnd === atEnd
        ? prev
        : { overflowing, atEnd },
    )
  }, [ref])

  useEffect(() => {
    const el = ref.current
    if (!el) return undefined
    el.addEventListener("scroll", measure, { passive: true })
    const observer =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null
    // observe() schedules an initial callback → first measurement (no
    // synchronous setState in this effect body).
    observer?.observe(el)
    return () => {
      el.removeEventListener("scroll", measure)
      observer?.disconnect()
    }
  }, [ref, measure])

  return state
}
